import { DateRange } from 'react-day-picker'
import type { Building } from '../types/building'
import { UpdatedPrices } from '../utils/checkout-prices'
import {
  AnalyticsDataLayer,
  AnalyticsProvider,
  AnalyticsService,
  PaymentMethod,
  ProductData,
  TrackAddToCartParams,
  TrackCtaClickParams,
  TrackEventParams,
  TrackFormParams,
  TrackPageParams,
  TrackPaymentInfo,
  TrackPurchaseEventParams,
  TrackTourFormParams,
  TrackViewItemParams,
  TrackEnterCheckoutFlowParams,
  TrackAddShippingInfo,
  TrackGeneralInquiryFormSubmitParams,
  TrackPurchaseCustomParams,
  TrackPricingInquirySubmitParams,
  TrackTabClickParams,
  TrackToggleClickParams,
  TrackFormStartParams,
  TrackEmailSubscribeFormParams,
  TrackMapCardClickParams,
  TrackPropertyResultClickParams,
  TrackContentCardClickParams,
  TrackNavigationMenuClickParams,
  TrackNavigationParams,
} from './analyticsService'
import dayjs from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import { getTotalDiscountsFromUpdatedPrices } from '../utils/discounts'
import { ProductSource, ProductType } from '../types/product'
import { productType } from '../utils/spaces'

dayjs.extend(timezone)
dayjs.extend(utc)

declare global {
  interface Window {
    dataLayer: Record<string, any>[]
  }
}

export class GoogleAnalyticsProvider implements AnalyticsProvider {
  dataLayer: Record<string, any>[] = []

  initialize() {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || []
      this.dataLayer = window.dataLayer
    }
  }

  trackPaymentMethod(paymentMethod: PaymentMethod) {
    this.pushDataLayer({
      paymentMethod,
    })
  }

  trackEvent({ category, action, label, value }: TrackEventParams) {
    this.pushDataLayer({
      event: 'customEvent',
      eventCategory: category,
      eventAction: action,
      eventLabel: label,
      eventValue: value,
    })
  }

  trackPageView(url: string) {
    this.pushDataLayer({
      event: 'pageView',
      pagePath: url,
    })
  }

  trackPricingInquiryForm(data: TrackPricingInquirySubmitParams): void {
    this.pushDataLayer({ ...data })
  }

  trackPage(data: TrackPageParams): void {
    this.pushDataLayer({
      content_type: data.content_type,
      content_id: data.content_id,
      page_name: data.page_name,
      page_url_clean: data.page_url_clean,
      page_variant: data.page_variant,
      business_region: data.business_region,
      site_name: data.site_name,
      navigation_variant: data.navigation_variant,
      footer_variant: data.footer_variant,
    })
  }

  trackViewItem(data: TrackViewItemParams): void {
    const [price, discount] = data.coupon ? [data.price, data.discount] : [0, 0]
    const region = this.getProductRegion(data.location).toLowerCase()
    const platformLocationId = this.getProductLocationId(data.location, data.source)
    const locationName = this.getProductLocationName(data.location)
    const productTypeName = this.getProductTypeName(data.type).toLowerCase()
    const itemName = this.generateItemName({
      productBuilding: locationName,
      productName: data.name,
      productType: data.type,
    }).toLowerCase()

    this.pushDataLayer({ ecommerce: null })
    this.pushDataLayer({
      event: 'view_item',
      ecommerce: {
        currency: data.currencyCode.toUpperCase(),
        value: 0,
        coupon: data.coupon ?? '',
        items: [
          {
            item_id: data.platformId,
            item_name: itemName,
            coupon: data.coupon ?? '',
            discount,
            price,
            quantity: 1,
            item_brand: 'your studio',
            item_category: productTypeName,
            item_category2: locationName.toLowerCase(),
            item_category3: data.bookingType,
            item_category4: region,
            item_category5: 'website purchase',
            location_id: platformLocationId,
            product_name: itemName,
            product_id: data.platformId,
            product_type: this.getProductTypeName(data.type).toLowerCase(),
            product_capacity: data.capacity,
            product_hourly_rate: data.hourlyRate,
            product_source: data.source,
          },
        ],
      },
    })
  }

  trackAddToCart(data: TrackAddToCartParams): void {
    const region = this.getProductRegion(data.location).toLowerCase()
    const platformLocationId = this.getProductLocationId(data.location, data.source)
    const locationName = this.getProductLocationName(data.location)
    const itemName = this.generateItemName({
      productBuilding: locationName,
      productName: data.name,
      productType: data.type,
    }).toLowerCase()
    const productTypeName = this.getProductTypeName(data.type).toLowerCase()
    this.pushDataLayerSync({
      event: 'add_to_cart',
      ecommerce: {
        currency: data.currencyCode.toUpperCase(),
        value: data.price,
        coupon: data.coupon ?? '',
        items: [
          {
            item_id: data.platformId,
            item_name: itemName,
            coupon: data.coupon ?? '',
            discount: data.discount,
            price: data.price,
            quantity: 1,
            item_brand: 'your studio',
            item_category: productTypeName,
            item_category2: locationName.toLowerCase(),
            item_category3: data.bookingType,
            item_category4: region,
            item_category5: 'website purchase',
            location_id: platformLocationId,
            product_name: itemName,
            product_id: data.platformId,
            product_type: productTypeName,
            product_capacity: data.capacity,
            product_hourly_rate: data.hourlyRate,
            product_source: data.source,
            booking_start_time: this.getBookingStartTime(data.dateRange),
            booking_end_time: this.getBookingEndTime(data.dateRange),
            booking_duration: this.getBookingDurationTime(data.dateRange),
            booking_date_selected: this.getBookingSelectedDates(data.dateRange),
          },
        ],
      },
    })
  }

  trackPurchaseEvent(data: TrackPurchaseEventParams): void {
    this.pushDataLayerSync({ ...data })
  }
  trackPurchaseCustom(data: TrackPurchaseCustomParams): void {
    this.pushDataLayerSync({ ...data })
  }

  trackEnterCheckoutFlow(data: TrackEnterCheckoutFlowParams): void {
    this.pushDataLayerSync({ ...data })
  }

  trackFormStart(data: TrackFormStartParams): void {
    this.pushDataLayer({ ...data })
  }

  trackEmailSubscribeForm(data: TrackEmailSubscribeFormParams): void {
    this.pushDataLayer({ ...data })
  }

  trackMapCardClick(data: TrackMapCardClickParams): void {
    this.pushDataLayer({ ...data })
  }

  generateItemName(data: ProductData): string {
    return `${data.productBuilding} | ${data.productName} | ${this.getProductTypeName(data.productType)}`
  }

  getTotalDiscount(data: UpdatedPrices): number {
    return getTotalDiscountsFromUpdatedPrices(data)
  }

  private buildings: Building[] = []

  setBuildings(buildings: Building[]) {
    this.buildings = buildings
  }

  private findBuilding(key: string) {
    return this.buildings.find((b) => b.key === key)
  }

  getProductRegion(building: string): string {
    return this.findBuilding(building)?.city.name ?? ''
  }

  getProductLocationId(building: string, source: ProductSource): string {
    const data = this.findBuilding(building)
    if (data) {
      switch (source) {
        case 'yardikube':
          return data.yardikubeLocationId ?? ''
        case 'tripleseat':
          return data.tripleseatLocationId ?? ''
      }
    }

    return ''
  }

  getProductLocationName(building: string): string {
    return this.findBuilding(building)?.name ?? ''
  }

  getProductTypeName(spaceType: ProductType): string {
    return productType[spaceType] ?? spaceType
  }

  getBookingStartTime(dateRange: DateRange): string {
    return dayjs(dateRange.from).format('HH:mm A')
  }

  getBookingEndTime(dateRange: DateRange): string {
    return dayjs(dateRange.to).format('HH:mm A')
  }

  getBookingDurationTime(dateRange: DateRange): number {
    const startTime = dayjs(dateRange.from)
    const endTime = dayjs(dateRange.to)
    return endTime.diff(startTime, 'minutes') / 60
  }

  getBookingSelectedDates(dateRange: DateRange): string {
    const startTime = dayjs(dateRange.from)
    const endTime = dayjs(dateRange.to)
    let selectedDates: string

    if (startTime.isSame(endTime, 'day')) {
      selectedDates = startTime.format('YYYY-MM-DD')
    } else {
      selectedDates = `${startTime.format('YYYY-MM-DD')} | ${endTime.format('YYYY-MM-DD')}`
    }

    return selectedDates
  }

  trackTourForm(data: TrackTourFormParams): void {
    this.pushDataLayer({
      event: data.event,
      event_origin: data.event_origin,
      event_vars: data.event_vars,
    })
  }

  trackGeneralInquiryForm(data: TrackGeneralInquiryFormSubmitParams): void {
    this.pushDataLayer({ ...data })
  }

  trackForm(data: TrackFormParams): void {
    this.pushDataLayer(data)
  }

  trackCtaClick(data: TrackCtaClickParams): void {
    this.pushDataLayer({
      event: 'cta_clicked',
      event_origin: 'front-end',
      event_vars: {
        cta_text: data.text.toLowerCase(),
        cta_style: data.style,
        link_url: data.href.startsWith('mailto:')
          ? 'contact email link'
          : data.href.startsWith('tel:')
          ? 'contact phone link'
          : data.href,
        component_name: data.component,
        page_url_clean: data.pageUrl,
      },
    })
  }

  pushDataLayer(data: AnalyticsDataLayer) {
    if (typeof window === 'undefined') return
    // Defer to next task so the browser can paint the interaction response first.
    // Safe for SPA navigation (Next.js client-side routing doesn't unload the page).
    setTimeout(() => window.dataLayer.push(data), 0)
  }

  pushDataLayerSync(data: AnalyticsDataLayer) {
    if (typeof window !== 'undefined') {
      window.dataLayer.push(data)
    }
  }

  trackPaymentInfo(data: TrackPaymentInfo): void {
    this.pushDataLayerSync({
      event: data.event,
      ecommerce: data.ecommerce,
    })
  }
  trackAddShippingInfo(data: TrackAddShippingInfo): void {
    this.pushDataLayerSync({
      event: data.event,
      ecommerce: data.ecommerce,
    })
  }

  trackTabClick(data: TrackTabClickParams) {
    this.pushDataLayer(data)
  }

  trackToggleClick(data: TrackToggleClickParams): void {
    this.pushDataLayer({ ...data })
  }

  trackPropertyResultClick(data: TrackPropertyResultClickParams): void {
    this.pushDataLayer(data)
  }

  trackContentCardClick(data: TrackContentCardClickParams): void {
    this.pushDataLayer(data)
  }
  trackNavigationMenuClick(data: TrackNavigationMenuClickParams): void {
    this.pushDataLayer(data)
  }

  trackNavigationParams(data: TrackNavigationParams): void {
    this.pushDataLayer(data)
  }
}

export default function startGoogleAnalyticsService(): AnalyticsService {
  const provider = new GoogleAnalyticsProvider()
  const googleAnalytics = new AnalyticsService(provider)
  return googleAnalytics
}
